import { defineStore } from 'pinia';
import { computed } from 'vue';
import type { CarrouselBanner } from '@/types/banners';

export const useBannersStore = defineStore('banners', () => {
  const state = useState('bannersState', () => ({
    banners: [] as CarrouselBanner[],
    isLoading: false,
    error: null as string | null
  }));
  const currentIndex = useState<number>('bannersCurrentIndex', () => 0);

  const config = useRuntimeConfig();
  const CFIMAGE_URL = config.public.CLOUDFLARE_HOST_URL;

  const processedBanners = computed(() => {
    if (!state.value.banners?.length) return [];
    return state.value.banners
      .map((banner) => ({
        ...banner,
        image_mobile: banner.image_mobile ?? banner.image,
      }))
      .filter((banner) => banner.image || banner.image_mobile);
  });

  function getUrlImageValid(image: string, display: string) {
    const cleanedCodeImage = image.replace(/-/g, '');
    const isCloudflareCode = cleanedCodeImage.length === 32;
    return isCloudflareCode ? `${CFIMAGE_URL}${image}/${display}` : image;
  }

  function getImageUrl(banner: CarrouselBanner, isMobile: boolean) {
    const display = isMobile ? 'banMobile' : 'banDesktop';
    if (isMobile) {
      return getUrlImageValid(banner.image_mobile || banner.image, display);
    }
    return getUrlImageValid(banner.image, display);
  }

  const fetchBanners = async () => {
    state.value.isLoading = true;
    state.value.error = null;
    try {
      const { data } = await useFetch<CarrouselBanner[]>('/v2/banners');
      state.value.banners = data.value || [];
    } catch (err) {
      state.value.error = 'Failed to fetch banners';
      console.error('Error fetching banners:', err);
    } finally {
      state.value.isLoading = false;
    }
  };

  const handleBannerClick = (banner: CarrouselBanner) => {
    if (banner.url) {
      window.open(banner.url, '_blank');
    }
  };

  const setCurrentIndex = (index: number) => {
    currentIndex.value = index;
  };

  return {
    state,
    processedBanners,
    currentIndex,
    getImageUrl,
    fetchBanners,
    handleBannerClick,
    setCurrentIndex,
  };
});